import { Anchor, Badge, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconCalendarEvent } from '@tabler/icons-react';
import { api } from '../../lib/api';
import { CATEGORY_META, addDaysYmd, berlinTodayYmd, splitLocal } from '../calendar/calendarModel';

interface Row {
  key: string;
  day: string;
  time: string | null;
  title: string;
  color: string;
  detail: string | null;
  cancelled: boolean;
  pending: boolean;
  href: string;
}

function dayLabel(day: string, today: string): string {
  if (day === today) return 'Heute';
  if (day === addDaysYmd(today, 1)) return 'Morgen';
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

/**
 * Startseite: was in den nächsten sieben Tagen für dich ansteht — Team-Termine, zu denen du
 * eingeladen bist, und deine Kundentermine. Steht nichts an, zeigt die Karte nichts.
 */
export function UpcomingCalendarWidget() {
  const navigate = useNavigate();
  const today = berlinTodayYmd();
  const { data } = useQuery({
    queryKey: ['calendar', 'upcoming'],
    queryFn: () => api.getCalendarUpcoming(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  if (!data) return null;

  const events = new Map(data.events.map((e) => [e.id, e]));
  const rows: Row[] = [];
  for (const o of data.occurrences) {
    const e = events.get(o.eventId);
    if (!e) continue;
    const start = splitLocal(o.startLocal);
    rows.push({
      key: o.key,
      day: start.date < today ? today : start.date,
      time: o.allDay ? null : start.time,
      title: e.title,
      color: CATEGORY_META[e.category].color,
      detail: e.audience !== 'PEOPLE' ? { TEAM: 'Ganzes Team', TECHNICIANS: 'Alle Techniker', LEADERSHIP: 'Leitung' }[e.audience] : e.location,
      cancelled: o.cancelled,
      pending: e.me?.response === 'PENDING',
      href: `/calendar?event=${e.id}&date=${o.occurrenceDate}`,
    });
  }
  for (const a of data.appointments) {
    const start = splitLocal(a.startLocal);
    rows.push({
      key: `appt:${a.id}`,
      day: start.date,
      time: start.time,
      title: a.title,
      color: 'cyan',
      detail: a.location ?? 'Kundentermin',
      cancelled: false,
      pending: false,
      href: '/appointments',
    });
  }
  if (rows.length === 0) return null;
  rows.sort((a, b) => `${a.day} ${a.time ?? '00:00'}`.localeCompare(`${b.day} ${b.time ?? '00:00'}`));

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <IconCalendarEvent size={18} />
          <Text fw={600}>Das steht an</Text>
          <Text size="xs" c="dimmed">
            nächste 7 Tage
          </Text>
        </Group>
        <Anchor size="xs" onClick={() => navigate('/calendar')}>
          Zum Kalender →
        </Anchor>
      </Group>
      <Stack gap={4}>
        {rows.slice(0, 8).map((r) => (
          <UnstyledButton key={r.key} onClick={() => navigate(r.href)}>
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed" w={72} style={{ flexShrink: 0 }}>
                {dayLabel(r.day, today)}
              </Text>
              <Text size="xs" c="dimmed" w={40} style={{ flexShrink: 0 }}>
                {r.time ?? 'ganzt.'}
              </Text>
              <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: `var(--mantine-color-${r.color}-6)` }} />
              <Text size="sm" truncate td={r.cancelled ? 'line-through' : undefined} style={{ flex: 1, minWidth: 0 }}>
                {r.title}
                {r.detail ? <Text span size="xs" c="dimmed">{` · ${r.detail}`}</Text> : null}
              </Text>
              {r.pending && (
                <Badge size="xs" variant="light" color="orange">
                  Antwort offen
                </Badge>
              )}
              {r.cancelled && (
                <Badge size="xs" variant="light" color="red">
                  abgesagt
                </Badge>
              )}
            </Group>
          </UnstyledButton>
        ))}
        {rows.length > 8 && (
          <Text size="xs" c="dimmed">
            … und {rows.length - 8} weitere
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
